import { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button, EmptyState, Field, FormDialog, Icon, Picker, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useClubSettings } from "../../app/hooks/useFanClub.js";
import { useComps, useGrantComp, useRevokeComp } from "../../app/hooks/useComps.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import styles from "./CompsManagerModal.module.scss";

export interface CompsManagerModalProps {
  onClose: () => void;
}

/** Cents → a plain dollar string (whole dollars where possible, else 2dp). */
function dollars(cents: number | undefined): string {
  const n = (cents ?? 0) / 100;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/**
 * The Antenna comps manager (routed `/antenna/comps`) — granting free access to a paid tier and
 * revoking it. Lists comps (GET /antenna/comps), grants (POST {email, tierId}), and revokes
 * (DELETE /antenna/comps?email=). The tier picker is sourced from the club's paid tiers (GET /antenna/club).
 *
 * Every mutation is confirm-then-repaint: an explicit pending state, server confirmation, the server
 * error surfaced VERBATIM inline, and the list repaints only on the invalidate → GET refetch (never optimistic).
 * Managing is `antenna:manage`; a read-only seat reaching the URL sees the forbidden state, not the manager
 * (the client mirror of the server 403). Born-blank: a fresh club renders the designed empty state.
 */
export function CompsManagerModal({ onClose }: CompsManagerModalProps) {
  const canManage = useCapability("antenna:manage");
  const settings = useClubSettings();
  const list = useComps();
  const grantMut = useGrantComp();
  const revokeMut = useRevokeComp();

  const [email, setEmail] = useState("");
  const [tierId, setTierId] = useState("");
  const [busy, setBusy] = useState(false);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);

  const paidTiers = (settings.data?.tiers ?? []).filter((t) => (t.priceCents ?? 0) > 0);

  // Default the tier select to the first paid tier once the club loads.
  useEffect(() => {
    if (tierId === "" && paidTiers.length > 0) setTierId(paidTiers[0].id ?? "");
  }, [tierId, paidTiers]);

  // `FormDialog` owns Escape, the focus trap and focus restore; it focuses its first focusable (the close
  // button), so claim the fan email field — the real entry point — once on open (parent effects run after the primitive's).
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  async function handleGrant() {
    if (!email.trim()) {
      setActionError("Enter a fan's email to grant a comp.");
      emailRef.current?.focus();
      return;
    }
    if (!tierId) {
      setActionError("Pick a tier for the comp.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await grantMut.mutateAsync({ email: email.trim(), tierId });
      // Confirm-then-repaint: cleared only after the server persisted; the list repaints on the invalidate.
      setEmail("");
    } catch (err) {
      setActionError(errorMessage(err, "Couldn't grant that comp. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(target: string) {
    setRevokingEmail(target);
    setActionError(null);
    try {
      await revokeMut.mutateAsync(target);
    } catch (err) {
      setActionError(errorMessage(err, "Couldn't revoke that comp. Please try again."));
    } finally {
      setRevokingEmail(null);
    }
  }

  const comps = list.data ?? [];

  return (
    <FormDialog
      title="Comped members"
      closeLabel="Close comps"
      // Each row acts on its own — nothing here is a draft to guard.
      dirty={false}
      size="lg"
      onClose={onClose}
      footer={
        canManage ? (
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        ) : undefined
      }
    >
      {!canManage ? (
        <div className={styles.notice} role="note">
          <p className={styles.noticeTitle}>Not available for your role</p>
          <p className={styles.noticeText}>
            Your role on this band doesn&apos;t include managing comps.
          </p>
          <Button variant="ghost" onClick={onClose}>
            Back to fan club
          </Button>
        </div>
      ) : (
        <>
          {/* GRANT FORM */}
          <div className={styles.addForm}>
            {paidTiers.length === 0 ? (
              <p className={styles.itemMeta}>
                Create a paid tier in Fan club settings before you can comp members into it.
              </p>
            ) : (
              <>
                <div className={styles.addGrid}>
                  <Field
                    ref={emailRef}
                    label="Fan email"
                    type="email"
                    placeholder="fan@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Picker
                    id="comp-tier"
                    label="Tier"
                    value={tierId}
                    onChange={setTierId}
                    options={paidTiers.map((t) => ({
                      value: t.id ?? "",
                      label: `${t.name || "Tier"} · ${dollars(t.priceCents)}`,
                    }))}
                  />
                </div>
                <div className={styles.addActions}>
                  <Button
                    variant="accent"
                    onClick={() => void handleGrant()}
                    loading={busy}
                    disabled={busy}
                  >
                    <Icon icon={Plus} size={16} /> Grant comp
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* LIST + states */}
          <p className={styles.sectionLabel}>Comped members</p>
          {list.isLoading ? (
            <CompsLoading />
          ) : list.isError ? (
            <CompsError error={list.error} onRetry={() => void list.refetch()} />
          ) : comps.length === 0 ? (
            <EmptyState
              title="No comps yet"
              description="Grant a superfan free access to a paid tier — they'll get it without paying."
            />
          ) : (
            <ul className={styles.list}>
              {comps.map((c) => {
                const target = c.email ?? "";
                const claimed = c.claimedOn != null;
                return (
                  <li key={target} className={styles.item}>
                    <div className={styles.itemMain}>
                      <span className={styles.itemTitle}>{target}</span>
                      <span className={styles.itemMeta}>{dollars(c.tierPriceCents)} tier · free</span>
                    </div>
                    <span className={styles.statusBadge} data-tone={claimed ? "success" : "neutral"}>
                      {claimed ? "Claimed" : "Unclaimed"}
                    </span>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      aria-label={`Revoke comp for ${target}`}
                      disabled={revokingEmail === target}
                      onClick={() => void handleRevoke(target)}
                    >
                      {revokingEmail === target ? <Spinner size={16} /> : <Icon icon={Trash2} size={16} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {actionError && (
            <p className={styles.error} role="alert">
              {actionError}
            </p>
          )}
        </>
      )}
    </FormDialog>
  );
}

/** Centered spinner while the comps load. */
function CompsLoading() {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <Spinner size={22} label="Loading comps…" />
      <p className={styles.centerText}>Loading comps…</p>
    </div>
  );
}

/** Load error: 403 → the forbidden state (the role mirror of the server deny); else the server detail + retry. */
function CompsError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) {
    return (
      <EmptyState
        title="Comps aren't available for your role"
        description="Your role on this band doesn't include the fan club."
      />
    );
  }
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState
      title="Couldn't load comps"
      description={message}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}
